import React, { useEffect, useState } from "react"

const STORAGE_KEYS_PREFIX = "merchant-portal_"
const useStorage =
  (storage: Storage, keyPrefix: string) =>
  <T>(storageKey: string, fallbackState: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
    if (!storageKey)
      throw new Error(`"storageKey" must be a nonempty string, but "${storageKey}" was passed.`)

    const storedString = storage.getItem(keyPrefix + storageKey)
    let parsedObject = null

    if (storedString !== null) parsedObject = JSON.parse(storedString)

    const [value, setValue] = useState<T>(parsedObject ?? fallbackState)

    useEffect(() => {
      storage.setItem(keyPrefix + storageKey, JSON.stringify(value))
    }, [value, storageKey])

    return [value, setValue]
  }

export const useLocalStorage = useStorage(localStorage, STORAGE_KEYS_PREFIX)

export const getSideBarCollapsedFromLocalStorage = (): boolean => {
  try {
    const storedString = localStorage.getItem(STORAGE_KEYS_PREFIX + "sidebar-collapsed")
    if (storedString === null) return false
    return JSON.parse(storedString)
  } catch {
    return false
  }
}

export const useReactiveLocalStorage = <T>(key: string, fallback: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [storedValue, setStoredValue] = useLocalStorage(key, fallback)

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS_PREFIX + key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue)
          setStoredValue(newValue)
        } catch {
          console.error(`Error parsing localStorage value for key "${key}"`)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key, setStoredValue])

  return [storedValue, setStoredValue]
}
